/**
 * Real API client — talks to the FastAPI backend at /v1/*.
 *
 * Typed thin wrappers around http(). Endpoint shapes mirror the backend
 * routers in app/api/*.
 *
 * Critical invariants:
 *   - The envelope contract is enforced server-side (app/middleware/envelope.py);
 *     these wrappers always receive {success, data, error, meta}. http()
 *     unwraps `data` on success and throws ApiError on `success=false`.
 *   - Title precedence cascade (BR-019): when displaying a session title,
 *     use long → short → fallback. Do not invert.
 *
 * Related ADRs: ADR-009 (React SSOT + Vue port discipline).
 * Related business rules: BR-019 (title precedence cascade), BR-013
 * (signed-URL TTL surfaced as `expires_in` on /add/signed-url).
 */
import { http, getToken, ApiError } from './http';

// ─── Auth ────────────────────────────────────────────────────────────────
export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export const auth = {
  login: (email: string, password: string) =>
    http<TokenResponse>('/v1/auth/login', {
      formBody: { username: email, password },
      anonymous: true,
    }),
  // noAuthHandling: this is the token-validation probe on app boot. A 401 here
  // means "the persisted token is stale" — bootstrap() owns that (clears it
  // only if a concurrent login() hasn't replaced it). It must NOT trip http()'s
  // global token-clear + /login redirect, which would clobber a fresh login.
  me: () => http<{ email: string; role?: string | null }>('/v1/auth/me', { noAuthHandling: true }),
};

// ─── Sessions ────────────────────────────────────────────────────────────
export interface SessionSummary {
  id: string;
  code: string;
  title: string;
  // extras2-manifest titles. Frontend cascades title_long > title_short > title
  // so the ugly auto-generated upload code never wins when the manifest
  // provided real metadata. Both nullable until the manifest parses.
  title_long: string | null;
  title_short: string | null;
  presenter: string | null;
  status: string;
  // The queue clock. ProcessingView counts elapsed from this, not from
  // when the browser tab opened — see its comment at queuedAt.
  created_at: string | null;
  duration_sec: number | null;
  word_count: number | null;
  segment_count: number | null;
  attendee_count: number | null;
  taxonomy: string[];
  // FK to session_types row chosen at ingest. Null until migration 044
  // backfill or until the operator picks one via SessionPatch. Drives
  // auto-init of session_stage_assignees from that Type's matrix.
  session_type_id?: string | null;
  // Transcript path used — drives the per-session pill on the Sessions list.
  transcript_provider?: string | null;   // builtin | assemblyai
  ai_pipeline?: string | null;            // direct (AI transcript) | enhanced (raw STT)
  // Per-session publishing destinations parsed from the manifest (slug→URL:
  // zoom/slides/podbean/vincast/intranet/mb_thread/session_page). Read-only.
  publishing_links?: Record<string, string>;
  // Live SOP stage from sop_state (LEFT JOIN in the LIST query only; the
  // detail endpoint omits it — per-session surfaces read sopApi.state).
  // Null when no sop_state row exists yet — render as 'prep' (the auto-init
  // value GET /sop would create). 2026-07-20: badges previously hardcoded
  // 'prep' on three surfaces because this field didn't exist.
  sop_stage?: string | null;
  // Task 2 (alignment truthfulness, 2026-09-10) — derived at read time by
  // GET /v1/sessions/{id} ONLY (app/api/sessions.py::get_session); the list
  // endpoint (GET /v1/sessions) does not compute it, so it is undefined
  // there — never treat a missing value as a false "not_applicable" or
  // "aligned" claim. One of: not_applicable | none | partial | aligned |
  // pending.
  alignment_state?: string | null;
  // Lane D task D2 (caption timing zero-gap, 2026-09-15) — derived at read
  // time by GET /v1/sessions/{id} ONLY, same as alignment_state above; the
  // list endpoint does not compute it, so it is undefined there. Never
  // render a missing value as either claim. One of: measured | estimated.
  //   measured  — enhanced chain, AssemblyAI, or a successful re-time.
  //   estimated — builtin+direct whose segment times ai_process.py invented
  //               from character proportions and no re-time has replaced.
  timing_source?: string | null;
  // Task 4 (verified transcript, 2026-09-29) — derived at read time by
  // GET /v1/sessions/{id} ONLY, same as the two fields above. NULL is the
  // backend saying it has no claim to make (non-ready session, a pipeline
  // the check cannot mean anything on, or the flag off): render nothing.
  // See TranscriptVerification below.
  transcript_verification?: TranscriptVerification | null;
}

// Mid-pipeline statuses — everything between upload and a terminal outcome.
// migrations/010_state_machine.sql's CHECK constraint enumerates the real
// values; 'ingesting' is kept here defensively (it was the pre-migration-010
// default and migration 010 normalizes it to 'uploading' on write, but it
// costs nothing to still recognize it as "processing" if it were ever seen).
// Any code that equality-checks `status === 'ingesting'` to mean "mid-
// pipeline" is checking a value the backend has not emitted since migration
// 010 landed — every real mid-pipeline session was silently falling through
// to a green "Ready" chip instead. Use this helper instead of a literal.
const PROCESSING_STATUSES = new Set([
  'ingesting', 'uploading', 'transcribing', 'normalizing', 'fusing', 'aligning',
]);
export function isProcessingStatus(status: string): boolean {
  return PROCESSING_STATUSES.has(status);
}

export interface SessionPatch {
  code?:            string;
  title?:           string;
  title_long?:      string;
  title_short?:     string;
  presenter?:       string;
  session_type_id?: string;
}

// One per-session, per-stage assignee row from
// GET /v1/sessions/{id}/stage-assignees. assignee_label survives renames
// because the backend joins typed FKs to people/groups at read time.
export interface SessionStageAssigneeRow {
  stage:                string;
  notify_email:         boolean;
  source:               'default' | 'manual';
  assigned_at:          string;
  person_id:            string | null;
  group_id:             string | null;
  person_email:         string | null;
  person_name:          string | null;
  person_role:          string | null;
  person_avatar_color:  string | null;
  group_name:           string | null;
  assignee_label:       string;
}

export interface SessionFilters {
  stage?: string;
  ai?: string;
  f?: string;
  limit?: number;
  offset?: number;
}

function _q(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export interface PipelineConfig {
  ai_pipeline: 'direct' | 'enhanced';
  ai_mode: string;
  ai_model: string;
  prompt_mode: string;
  custom_prompt?: string | null;
  stt_backend: string;
  template_id: string;
  transcript_provider?: 'builtin' | 'assemblyai';
  // Provider-correct display strings derived server-side (single source of
  // truth) so the UI shows the real engine/model — e.g. "AssemblyAI STT ·
  // universal-3-pro" / "AssemblyAI LLM Gateway · claude-sonnet-4-6" vs
  // "Google STT · latest_long" / "Gemini · 2.5-pro". Null on older responses.
  effective_stt_engine?: string | null;
  effective_ai_model?: string | null;
  iil_config: { enabled: boolean; tier1: boolean; tier2: boolean; tier3: boolean };
  // T1 v2 (2026-08-14) — per-session SRT-chunking override. null = inherit
  // the org_settings default (see app/api/exports.py's precedence chain).
  srt_chunk_enabled?: boolean | null;
  srt_chunk_max_words?: number | null;
}

export interface PipelineConfigPatch {
  srt_chunk_enabled?: boolean | null;
  srt_chunk_max_words?: number | null;
}

export interface SessionFailureReason {
  session_id: string;
  code: string;
  title: string;
  status: string;
  reason: string | null;
  category: string | null;
  ts: string | null;
  actor: string | null;
  log_tail: Array<Record<string, unknown>>;
}

export interface DeletedSessionRow {
  session_id: string;
  code: string;
  title: string;
  presenter: string | null;
  status: string;
  created_at: string | null;
  deleted_at: string | null;
}

export interface SrtCue { index: number; start_ms: number; end_ms: number; text: string; }
/**
 * C6 (2026-09-15) — what the caption builder ACTUALLY produced, per build.
 * The three timebase counts partition the cues and sum to `total_cues`;
 * `degenerate_cues` overlaps them (a cue too short to read, whatever timed
 * it). The SRT tab's badge is driven from these, never from row existence.
 */
export interface SrtTiming {
  total_cues: number;
  word_timed_cues: number;
  interpolated_cues: number;
  estimated_cues: number;
  out_of_order_cues: number;
  degenerate_cues: number;
  /** A MEASURED cue whose real start precedes the cue before it — the signal
   *  that a word-timed build is not clean. Optional because a session cached
   *  from before this field shipped will not carry it; SrtPane treats a
   *  missing count as 0, never as an error. */
  inverted_cues?: number;
  timing_drop_reasons: Record<string, number>;
}
export interface SrtCuesResponse {
  cues: SrtCue[];
  count: number;
  /** Whether the AssemblyAI store HAS rows. A fact about the store, NOT
   *  about the build — read `timing` for what the build achieved. */
  source: 'assemblyai' | 'gemini';
  timing?: SrtTiming;
}
export interface SrtReconcileWord { text: string; matched: boolean; }
export interface SrtReconcileBlock {
  seq: number;
  a_words: SrtReconcileWord[];   // what AssemblyAI heard (raw), this segment
  b_words: SrtReconcileWord[];   // edited transcript, this segment
  similarity: number;
}
export interface SrtReconcile {
  blocks: SrtReconcileBlock[];
  similarity: number;
  diff_count: number;
}

export const sessions = {
  list: (filters: SessionFilters = {}) =>
    http<SessionSummary[]>(`/v1/sessions${_q({ ...filters })}`),
  get: (id: string) =>
    http<SessionSummary>(`/v1/sessions/${encodeURIComponent(id)}`),
  create: (payload: { code: string; title: string; presenter?: string; duration_sec?: number | null; attendee_count?: number | null; taxonomy?: string[]; pipeline_config?: PipelineConfig }) =>
    http<SessionSummary>('/v1/sessions', { body: payload, method: 'POST' }),
  update: (id: string, patch: SessionPatch) =>
    http<SessionSummary>(`/v1/sessions/${encodeURIComponent(id)}`, { body: patch, method: 'PATCH' }),
  stageAssignees: (id: string) =>
    http<SessionStageAssigneeRow[]>(`/v1/sessions/${encodeURIComponent(id)}/stage-assignees`),
  setStageAssignee: (id: string, stage: string, body: {
    assignee_email?: string;
    person_id?:      string;
    group_id?:       string;
    notify_email?:   boolean;
  }) =>
    http<SessionStageAssigneeRow>(
      `/v1/sessions/${encodeURIComponent(id)}/stage-assignees/${encodeURIComponent(stage)}`,
      { body, method: 'PUT' },
    ),
  applyTypeDefaults: (id: string, typeId?: string) =>
    http<{ session_id: string; stages: number }>(
      `/v1/sessions/${encodeURIComponent(id)}/stage-assignees/apply-type-defaults${
        typeId ? `?type_id=${encodeURIComponent(typeId)}` : ''
      }`,
      { method: 'POST' },
    ),
  remove: (id: string) =>
    http<{ session_id: string; deleted: boolean }>(`/v1/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  listDeleted: () =>
    http<DeletedSessionRow[]>('/v1/sessions/deleted'),
  restore: (id: string) =>
    http<{ session_id: string; restored: boolean }>(`/v1/sessions/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  permanentDelete: (id: string) =>
    http<{ session_id: string; permanently_deleted: boolean }>(`/v1/sessions/${encodeURIComponent(id)}/permanent`, { method: 'DELETE' }),
  failureReason: (id: string) =>
    http<SessionFailureReason>(`/v1/sessions/${encodeURIComponent(id)}/failure-reason`),
  // Phase 2 (2026-09-03) — SRT tab. Cues of the downloadable .srt (AssemblyAI-
  // timed, or Gemini fallback). Same source of truth as GET /exports/srt.
  srtCues: (id: string) =>
    http<SrtCuesResponse>(`/v1/sessions/${encodeURIComponent(id)}/srt-cues`),
  // Phase 3 (2026-09-03) — regenerate the AssemblyAI SRT pass on demand.
  // C6 (2026-09-15): the reply states only that the rebuild reached the
  // queue. It is NOT a report that the captions were rebuilt.
  regenerateSrt: (id: string) =>
    http<{ status: string; task_id?: string }>(`/v1/sessions/${encodeURIComponent(id)}/srt-cues/regenerate`, { method: 'POST' }),
  // Phase 3 — reconciliation: SRT text vs edited transcript, word-flagged.
  srtReconcile: (id: string) =>
    http<SrtReconcile>(`/v1/sessions/${encodeURIComponent(id)}/srt-reconcile`),
  pipelineConfig: (id: string) =>
    http<PipelineConfig & { auto_detected_template_id: string | null; auto_detected_confidence: number | null }>(
      `/v1/sessions/${encodeURIComponent(id)}/pipeline-config`,
    ),
  // T1 v2 — per-session SRT-chunking override. Whitelisted server-side to
  // exactly srt_chunk_enabled / srt_chunk_max_words (admin-gated).
  updatePipelineConfig: (id: string, patch: PipelineConfigPatch) =>
    http<PipelineConfig & { auto_detected_template_id: string | null; auto_detected_confidence: number | null }>(
      `/v1/sessions/${encodeURIComponent(id)}/pipeline-config`,
      { body: patch, method: 'PATCH' },
    ),
  retry: (id: string) =>
    http<{ session_id: string; status_before: string; enqueued: boolean; detail?: string }>(
      `/v1/diag/reingest/${encodeURIComponent(id)}`,
      { method: 'POST' },
    ),
  // Session-files panel (MIC add_to_session.py port)
  missing: (id: string) =>
    http<{ has_slides: boolean; has_chat: boolean; has_manifest: boolean; has_bios: boolean }>(
      `/v1/sessions/${encodeURIComponent(id)}/missing`,
    ),
  addSignedUrl: (id: string, body: { filename: string; mime_type: string; type: 'slides' | 'chat' | 'manifest' }) =>
    http<{ signed_url: string; gcs_uri: string; blob_name: string; mime_type: string; expires_in: number }>(
      `/v1/sessions/${encodeURIComponent(id)}/add/signed-url`,
      { body, method: 'POST' },
    ),
  addSlides: (id: string, body: { gcs_uri: string; slide_numbers?: number[] }, mode?: 'replace' | 'append' | 'replace_selected') =>
    http(`/v1/sessions/${encodeURIComponent(id)}/add/slides${mode ? `?mode=${mode}` : ''}`, { body, method: 'POST' }),
  addChat: (id: string, body: { gcs_uri: string }, opts: { confirm?: boolean; start_time?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.confirm) qs.set('confirm', 'true');
    if (opts.start_time) qs.set('start_time', opts.start_time);
    const q = qs.toString();
    return http(`/v1/sessions/${encodeURIComponent(id)}/add/chat${q ? `?${q}` : ''}`, { body, method: 'POST' });
  },
  addManifest: (id: string, body: { gcs_uri: string }, mode?: 'use_new' | 'keep_current') =>
    http(`/v1/sessions/${encodeURIComponent(id)}/add/manifest${mode ? `?mode=${mode}` : ''}`, { body, method: 'POST' }),
  // Phase 10.1 — caption burn-in to MP4
  burnCaptions: (id: string, styleConfig?: Record<string, unknown>) =>
    http<{ enqueued: boolean; session_id: string }>(
      `/v1/sessions/${encodeURIComponent(id)}/captions/burn`,
      { body: { style_config: styleConfig || null }, method: 'POST' },
    ),
  captionedVideo: (id: string) =>
    http<{
      artifact_id: string; gcs_uri: string; download_url: string | null;
      bytes: number | null; version: number; is_current: boolean;
      generated_at: string | null; style_config: Record<string, unknown> | null;
    } | null>(`/v1/sessions/${encodeURIComponent(id)}/captioned-video`),
  // Chat participant tally (Phase 3 of the 2026-06-04 stakeholder remediation).
  // Aggregates chat_messages by author. Empty array for sessions without chat.
  chatParticipants: (id: string) =>
    http<ChatParticipantRow[]>(
      `/v1/sessions/${encodeURIComponent(id)}/chat-participants`,
    ),
};

export interface ChatParticipantRow {
  speaker:       string;
  message_count: number;
  first_seen_ms: number;
  last_seen_ms:  number;
}

// ─── Speakers (Phase 9 — per-session speaker CRUD + bulk segment reassign) ──
export interface SessionSpeaker {
  id: string;
  short: string | null;
  name: string | null;
  role: string | null;
  avatar_color?: string | null;
}

// ─── Discrepancies (LCS-detected AI ↔ STT diffs, classified by Gemini) ──
export interface DiscrepancyRow {
  id:               string;
  segment_id:       string | null;
  ai_text:          string | null;
  stt_text:         string | null;
  category:         string | null;   // medication | terminology | filler | punctuation | drift | low_confidence | other
  is_meaningful:    boolean | null;  // null until classify runs
  classifier_model: string | null;
  classified_at:    string | null;
  created_at:       string | null;
}

export interface DiscrepancyListResponse {
  session_id:            string;
  count:                 number;
  classified_count:      number;
  classification_status: 'complete' | 'partial' | 'pending';
  discrepancies:         DiscrepancyRow[];
}

export const discrepancies = {
  list: (sessionId: string, opts: { category?: string; meaningful_only?: boolean } = {}) => {
    const q = _q(opts as Record<string, string | number | undefined>);
    return http<DiscrepancyListResponse>(`/v1/sessions/${encodeURIComponent(sessionId)}/discrepancies${q}`);
  },
};


// ─── Words (real Google STT per-token data from `words` table) ───────────
export interface WordRow {
  id:          string;
  segment_id:  string;
  seq:         number;
  word:        string;
  start_ms:    number;
  end_ms:      number;
  confidence:  number;
}

export const words = {
  listBySession: (sessionId: string) =>
    http<WordRow[]>(`/v1/sessions/${encodeURIComponent(sessionId)}/words`),
};


// ─── Word alignment (L2 highlight — per-Gemini-word STT timestamps) ──────
// Populated by lcs_discrepancies_task. Every Gemini word in the segment
// gets a row; matched words carry real STT timestamps, unmatched words
// carry nulls and the highlighter skips them.
export interface WordAlignmentEntry {
  g: number;                 // 0-based index into seg.text.split() (Gemini token)
  s: number | null;          // stt_start_ms — null when match_kind='unmatched'
  e: number | null;          // stt_end_ms — null when match_kind='unmatched'
  k: 'exact' | 'unmatched';  // match_kind
}

export interface WordAlignmentResponse {
  session_id: string;
  count:      number;
  matched:    number;
  segments:   Record<string, WordAlignmentEntry[]>;
}

export const wordAlignment = {
  get: (sessionId: string) =>
    http<WordAlignmentResponse>(`/v1/sessions/${encodeURIComponent(sessionId)}/word-alignment`),
};


// ─── transcript verification ────────────────────────────────────────────
// Plan: .superpowers/sdd/2026-09-29-verified-transcript. On the
// builtin+direct path the whole transcript is ONE Gemini call, so a dropped
// passage is invisible — the surviving text still tiles the recording end to
// end. The verification pass compares the transcript against the independent
// speech-to-text words and records ONE verdict per run.

/** The `transcript_verification` projection on GET /v1/sessions/{id}. */
export interface TranscriptVerification {
  // pending | verified_complete | verified_incomplete | verified_repaired |
  // verified_partial | not_assessable | error. The whole field is null (and
  // renders nothing) for every session the backend cannot claim anything
  // about, so an unknown string here is likewise no claim — see
  // SessionDetailView's verificationChip.
  state:               string;
  as_of:               string | null;
  // Null, never 0, until a row exists: "nobody looked" and "we looked and
  // found none" are different answers.
  holes_recovered:     number | null;
  holes_untranscribed: number | null;
  // Every hole in the row, whatever its outcome — the count a
  // `verified_incomplete` chip reads, where the two counters above are 0.
  holes_total:         number | null;
  unsupported:         number | null;
  stt_words:           number | null;
  // The transcript changed after the check — a correction or an inserted
  // segment postdates the verdict, so it describes text that has moved on.
  edited_since:        boolean;
  // `not_assessable`: provider_not_builtin | pipeline_not_direct |
  // no_stt_words | unsupported_runs | not_ready.
  // `error`: verification_raised — a fixed label, never the exception text.
  // Null for every other state.
  reason:              string | null;
}

/** One window of speech the transcript does not account for. */
export interface VerificationHole {
  start_ms:      number | null;
  end_ms:        number | null;
  stt_words:     number | null;
  missing_words: number | null;
  owner_index:   number | null;
  // detected (verify-only) | recovered | untranscribed | declined_edited |
  // declined_locked | declined_present | declined_missing_owner |
  // skipped_existing | over_cap | repair_unavailable (the repair branch).
  // VerificationBanner renders nothing for a value outside this list.
  outcome:       string | null;
  // owner_index resolved through the seq-ordered segment list, or null when
  // the index no longer addresses a segment (merge/split/delete since).
  segment_id:    string | null;
}

/** A run of transcript text with no speech-to-text support behind it. */
export interface VerificationUnsupportedRun {
  segment_index: number | null;
  token_count:   number | null;
}

/** GET /v1/sessions/{id}/verification — the windows, not just the verdict. */
export interface VerificationResponse {
  session_id:       string;
  // Null means "no claim" — the surface renders nothing.
  state:            string | null;
  reason:           string | null;
  as_of:            string | null;
  edited_since:     boolean;
  stt_words:        number | null;
  transcript_words: number | null;
  holes:            VerificationHole[];
  unsupported:      VerificationUnsupportedRun[];
}

export const verification = {
  get: (sessionId: string) =>
    http<VerificationResponse>(`/v1/sessions/${encodeURIComponent(sessionId)}/verification`),
};


export const speakers = {
  list: (sessionId: string) =>
    http<SessionSpeaker[]>(`/v1/sessions/${encodeURIComponent(sessionId)}/speakers`),
  add: (sessionId: string, body: { name: string; role?: string; avatar_color?: string }) =>
    http<SessionSpeaker>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/speakers`,
      { body, method: 'POST' },
    ),
  edit: (sessionId: string, speakerId: string, body: { name?: string; role?: string; avatar_color?: string }) =>
    http<SessionSpeaker>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/speakers/${encodeURIComponent(speakerId)}`,
      { body, method: 'PATCH' },
    ),
  remove: (sessionId: string, speakerId: string) =>
    http<void>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/speakers/${encodeURIComponent(speakerId)}`,
      { method: 'DELETE' },
    ),
  reassignSegment: (sessionId: string, segmentId: string, speakerId: string) =>
    http<SessionSpeaker>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/segments/${encodeURIComponent(segmentId)}/speaker-reassign`,
      { body: { speaker_id: speakerId }, method: 'POST' },
    ),
  // Speaker merge / rename-all: repoint every segment off the source speakers
  // onto the target (canonical) speaker, optionally rename the target, then
  // delete the emptied sources. dry_run returns the impact without mutating.
  // Batches of <= undo_max snapshot prior values + the deleted speakers and
  // return undoable:true; larger ones return undoable:false (UI warns first).
  merge: (
    sessionId: string,
    body: { source_speaker_ids: string[]; target_speaker_id: string; rename_to?: string; dry_run?: boolean },
  ) =>
    http<{
      dry_run?: boolean;
      affected_count?: number;
      source_names?: string[];
      target_name?: string;
      batch_id?: string;
      merged?: number;
      target_speaker_id?: string;
      removed_speakers: number;
      undoable?: boolean;
      undo_max?: number;
    }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/speakers/merge`,
      { body, method: 'POST' },
    ),
  mergeUndo: (sessionId: string, batchId: string) =>
    http<{ batch_id: string; restored: number; speakers_restored: number }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/speakers/merge/${encodeURIComponent(batchId)}/undo`,
      { method: 'POST' },
    ),
};

export interface MediaUrl {
  role: string;
  filename: string | null;
  content_type: string | null;
  duration_sec: number | null;
  url: string;
}

export const media = {
  url: (sessionId: string, role: 'audio' | 'video' = 'audio') =>
    http<MediaUrl>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/media-url?role=${role}`,
    ),
};

export interface QueueItem {
  session_id:         string;
  code:               string;
  title:              string | null;
  title_short:        string | null;
  title_long:         string | null;
  status:             string;
  current_stage:      string;
  entered_current_at: string | null;
  overdue_hours:      number | null;
}

export const queue = {
  // Phase 7-broader (2 of 2) — per-user queue. Sessions where the
  // current user is the assignee for the session's current SOP stage.
  // Backend computes overdue_hours server-side.
  mine: () => http<QueueItem[]>('/v1/queue/mine'),
};

export const placements = {
  chatAnchor: (sessionId: string, chatId: string, anchorSegment: string | null) =>
    http<unknown>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/chat/${encodeURIComponent(chatId)}`,
      { body: { anchor_segment: anchorSegment }, method: 'PATCH' },
    ),
  pollAnchor: (sessionId: string, pollId: string, anchorSegment: string | null) =>
    http<unknown>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/polls/${encodeURIComponent(pollId)}/anchor`,
      { body: { anchor_segment: anchorSegment }, method: 'PATCH' },
    ),
  // Phase 6 — bulk reorder for chat + polls. Send the new desired
  // order of row ids; backend sets order_index = position (1-indexed)
  // and writes a single audit_events row for the reorder action.
  chatReorder: (sessionId: string, ids: string[]) =>
    http<{ reordered: number; ids: string[] }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/chat/order`,
      { body: { ids }, method: 'PATCH' },
    ),
  pollsReorder: (sessionId: string, ids: string[]) =>
    http<{ reordered: number; ids: string[] }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/polls/order`,
      { body: { ids }, method: 'PATCH' },
    ),
};


// ─── Exports (Phase A1 — wire DownloadMenu) ───────────────────────────
// Backend: GET /v1/sessions/{id}/exports/{format} returns the artifact
// bytes with Content-Disposition: attachment. The http() wrapper assumes
// JSON, so this helper does a raw fetch + blob conversion + manual
// JWT header, then streams the blob through a transient <a download>
// element. The object URL is revoked immediately after click to free
// memory.
export interface SrtChunkQuery {
  /** Query-param escape hatch mirroring the backend precedence chain (query
   * param > session override > org default). Undefined = don't send it —
   * callers that already PATCHed the per-session override (DownloadMenu)
   * leave both undefined so the download URL carries no state of its own. */
  chunk?: boolean;
  chunkWords?: number;
}

export const exports_ = {
  /** Download an export artifact and trigger a browser save dialog. */
  download: async (
    sessionId: string,
    format: 'docx' | 'srt' | 'vtt' | 'html' | 'txt' | 'zip',
    variant?: string,
    srtChunk?: SrtChunkQuery,
  ): Promise<void> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const params = new URLSearchParams();
    if (variant) params.set('variant', variant);
    if (srtChunk?.chunk !== undefined) params.set('chunk', String(srtChunk.chunk));
    if (srtChunk?.chunkWords !== undefined) params.set('chunk_words', String(srtChunk.chunkWords));
    const qs = params.toString();
    const url = `/v1/sessions/${encodeURIComponent(sessionId)}/exports/${encodeURIComponent(format)}${qs ? `?${qs}` : ''}`;
    const resp = await fetch(url, { headers });
    if (!resp.ok) {
      let envelope: unknown = null;
      try { envelope = await resp.json(); } catch { /* non-JSON */ }
      throw new ApiError(resp.status, envelope);
    }
    // Filename from Content-Disposition; fall back to session_id.format.
    const cd = resp.headers.get('content-disposition') || '';
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1] || `${sessionId}.${format}`;
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objUrl);
  },

  // Phase C2 — fetch the session's WebVTT captions and return a Blob
  // URL so the editor's <track src> can play them. We can't use the
  // raw /captions.vtt URL directly because <track> can't send the
  // Authorization header — the blob URL is a local reference, no auth
  // crosses the wire on the <track> side. The server still ETag-caches
  // (Phase C1), so the underlying fetch is cheap on repeat loads.
  // Returns null on 404 (session has no captions yet) so the caller
  // can hide CC controls gracefully.
  captionsBlobUrl: async (sessionId: string): Promise<string | null> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const url = `/v1/sessions/${encodeURIComponent(sessionId)}/captions.vtt`;
    const resp = await fetch(url, { headers });
    if (resp.status === 404) return null;
    if (!resp.ok) {
      let envelope: unknown = null;
      try { envelope = await resp.json(); } catch { /* non-JSON */ }
      throw new ApiError(resp.status, envelope);
    }
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  },
};
// Re-export under the canonical name; `exports` is a reserved-ish JS word
// in some bundlers, so the internal symbol uses an underscore suffix.
export { exports_ as exportsApi };


// ─── Corrections (Phase 4 — append-only ledger with undo/redo pointer) ──
export interface CorrectionRow {
  correction_id: string;
  sequence_number: number;
  action_id: string;
  segment_id: string;
  correction_type:
    | 'slide_reassignment' | 'text_edit' | 'split' | 'merge' | 'mark_ok'
    | 'chat_insert' | 'chat_edit' | 'chat_remove'
    | 'poll_insert' | 'poll_remove'
    | 'speaker_reassignment' | 'segment_delete';
  old_slide_id: string | null;
  new_slide_id: string | null;
  old_text: string | null;
  new_text: string | null;
  applied_at: string | null;
  applied_by: string;
  active: boolean;
}

export interface CorrectionApplied {
  correction_id: string;
  sequence_number: number;
  action_id: string;
  segment_id: string;
  correction_type: CorrectionRow['correction_type'];
  old_slide_id: string | null;
  new_slide_id: string | null;
  old_text: string | null;
  new_text: string | null;
  applied_at: string | null;
  applied_by: string;
  resolved_discrepancy_id: string | null;
  // Set (HTTP 200, no ledger row) when expected_content_hash no longer
  // matched: a split or merge rewrote the passage, and the write was DROPPED
  // (app/api/corrections.py). Not a success.
  stale?: boolean;
}

export interface FindReplaceResult {
  session_id: string;
  find: string;
  replace: string;
  case_sensitive: boolean;
  matches: Array<{ segment_id: string; old_text: string; new_text: string; match_count: number }>;
  total_matches: number;
  segment_count: number;
  applied: boolean;
  action_id: string | null;
  corrections: CorrectionApplied[];
}

export const corrections = {
  apply: (
    sessionId: string,
    body: {
      segment_id: string;
      correction_type: CorrectionRow['correction_type'];
      old_slide_id?: string | null;
      new_slide_id?: string | null;
      old_text?: string | null;
      new_text?: string | null;
      action_id?: string | null;
      // Autosave-vs-split race guard (backend C6). When set on a text_edit,
      // the write is conditional on the segment's content_hash still matching;
      // a stale autosave (a split/merge rewrote the row) is dropped server-side
      // instead of clobbering the post-split text.
      expected_content_hash?: string | null;
    },
  ) =>
    http<CorrectionApplied>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections`,
      { body, method: 'POST' },
    ),
  list: (sessionId: string) =>
    http<{ session_id: string; current_pointer: number; corrections: CorrectionRow[] }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections`,
    ),
  undo: (sessionId: string) =>
    http<{ session_id: string; pointer: number; action?: string }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections/undo`,
      { method: 'POST' },
    ),
  redo: (sessionId: string) =>
    http<{ session_id: string; pointer: number; action?: string }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections/redo`,
      { method: 'POST' },
    ),
  findReplace: (
    sessionId: string,
    body: { find: string; replace: string; case_sensitive?: boolean; dry_run?: boolean },
  ) =>
    http<FindReplaceResult>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/find-replace`,
      { body, method: 'POST' },
    ),
  reviewQueue: (sessionId: string) =>
    http<{
      session_id: string;
      count: number;
      items: Array<{
        segment_id: string; alignment_id: string; status: string;
        confidence: number | null; drift_flag: boolean; uncertain_flag: boolean;
        slide_id: string | null; priority_score: number;
      }>;
    }>(`/v1/sessions/${encodeURIComponent(sessionId)}/review-queue`),

  // Phase 3.5 + 4 (split/merge) — typed convenience wrappers for the two
  // correction types that the generic apply() helper above can't model
  // ergonomically. Both POST to the same /v1/sessions/{sid}/corrections
  // endpoint with the structural payload the backend executor expects.
  // Behind backend feature flag SPLIT_MERGE_ENABLED — disabled flag yields
  // 503 SPLIT_MERGE_DISABLED. Frontend gates via featureFlags store too.
  splitSegment: (
    sessionId: string,
    segmentId: string,
    afterWordIndex: number,
    actionId?: string,
  ) =>
    http<CorrectionApplied>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections`,
      {
        body: {
          segment_id: segmentId,
          correction_type: 'split',
          after_word_index: afterWordIndex,
          ...(actionId ? { action_id: actionId } : {}),
        },
        method: 'POST',
      },
    ),
  mergeSegment: (
    sessionId: string,
    segmentId: string,
    expectedRightSegmentId: string,
    actionId?: string,
  ) =>
    http<CorrectionApplied>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections`,
      {
        body: {
          segment_id: segmentId,
          correction_type: 'merge',
          expected_right_segment_id: expectedRightSegmentId,
          ...(actionId ? { action_id: actionId } : {}),
        },
        method: 'POST',
      },
    ),
  deleteSegment: (
    sessionId: string,
    segmentId: string,
    actionId?: string,
  ) =>
    http<CorrectionApplied>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/corrections`,
      {
        body: {
          segment_id: segmentId,
          correction_type: 'segment_delete',
          ...(actionId ? { action_id: actionId } : {}),
        },
        method: 'POST',
      },
    ),
};


// ─── Segments ────────────────────────────────────────────────────────────
export interface SegmentRow {
  id: string;
  seq: number;
  start_ms: number;
  end_ms: number;
  text: string;
  confidence: number | null;
  flags: string[];
  is_anchor: boolean;
  anchor_kind: string | null;
  slide_id: string | null;
  speaker_id: string | null;
  content_hash: string | null;
}

export const segments = {
  list: (sessionId: string) =>
    http<SegmentRow[]>(`/v1/sessions/${encodeURIComponent(sessionId)}/segments`),
  edit: (sessionId: string, segmentId: string, patch: Partial<Pick<SegmentRow, 'text' | 'slide_id' | 'speaker_id' | 'flags'>>) =>
    http<SegmentRow>(`/v1/sessions/${sessionId}/segments/${segmentId}`, { body: patch, method: 'PATCH' }),
  reassign: (sessionId: string, segmentId: string, slideId: string) =>
    http<SegmentRow>(`/v1/sessions/${sessionId}/segments/${segmentId}/reassign`, { body: { slide_id: slideId }, method: 'POST' }),
  // Bulk reassign speaker and/or slide across many selected segments in one
  // set-based call. Batches of <= undo_max snapshot prior values and return
  // undoable:true; larger batches return undoable:false (UI warns first).
  bulkReassign: (
    sessionId: string,
    segmentIds: string[],
    opts: { speaker_id?: string; slide_id?: string },
  ) =>
    http<{ batch_id: string; reassigned: number; kind: string; undoable: boolean; undo_max: number }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/segments/bulk-reassign`,
      { body: { segment_ids: segmentIds, ...opts }, method: 'POST' },
    ),
  bulkReassignUndo: (sessionId: string, batchId: string) =>
    http<{ batch_id: string; restored: number }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/segments/bulk-reassign/${encodeURIComponent(batchId)}/undo`,
      { method: 'POST' },
    ),
  // Vertical drag-drop reorder: send the FULL new order of the session's
  // segment IDs. seq-only rewrite; always undoable (undo via bulkReassignUndo,
  // which restores prior seq for reorder batches).
  reorder: (sessionId: string, orderedSegmentIds: string[]) =>
    http<{ batch_id: string | null; reordered: number; kind: string; undoable: boolean }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/segments/reorder`,
      { body: { ordered_segment_ids: orderedSegmentIds }, method: 'POST' },
    ),
};

// ─── SOP ─────────────────────────────────────────────────────────────────
// One row of GET /v1/sessions/{id}/sop/transitions (sop_transitions table).
// from_stage is null on the auto-init row (created → prep).
export interface SopTransitionRow {
  from_stage:  string | null;
  to_stage:    string;
  actor_email: string;
  note:        string | null;
  occurred_at: string;
}

// Stage-scoped annotation appended via PATCH /sop/annotations and returned
// by GET /sop (read path added 2026-07-25 — annotations were write-only).
export interface SopAnnotation {
  stage: string;
  kind: 'note' | 'override' | 'blocker' | string;
  body: string;
  actor_email: string;
  inserted_at: string;
}

// SOP stage/check definitions (Workflow Admin Panel data layer, migration
// 072). check_id is the immutable slug identity — callers attest against
// it directly and must never re-derive it from `label` (an admin reword
// must not orphan an attestation).
export interface SopCheckDefRow {
  // Workflow Admin Panel Task 8: the sop_check_defs row's UUID -- the
  // PATCH /v1/sop/defs/checks/{id} target. Never the same value as
  // check_id (the immutable slug attestations join on).
  id: string;
  check_id: string;
  label: string;
  sort_order: number;
  // Task 8 fix round 1: PRESENT (explicit true/false) only when the
  // request that produced this payload passed includeInactive; on the
  // default request (SopView/useSopDefs -- active checks only) this key
  // is always absent, and a row's mere presence still implies active.
  is_active?: boolean;
  // Per-type workflows Task 9a: present ONLY on GET /v1/sop/defs's typed
  // branch -- this check's owning type when it came from an override/
  // type-only row, else null for an untouched inherited row. Absent (never
  // sent) on the no-type response, exactly like has_*_override above.
  session_type_id?: string | null;
}
// Full check-def row shape (POST /checks and PATCH /checks/{id} responses,
// and the checks[] array inside POST /stages/{key}/restore's response) --
// a superset of SopCheckDefRow that also carries is_active/version/
// stage_key, needed by the checks editor's add/reword/deactivate flows.
export interface SopCheckDefFull {
  id: string;
  stage_key: string;
  check_id: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  session_type_id: string | null;
  version: number;
  updated_by: string | null;
  updated_at: string | null;
}
// POST /v1/sop/defs/checks body. Controller ruling (Task 8): the admin
// panel posts label only -- check_id is never client-derived or sent; the
// server mints it via _check_slug and the response's `check_id` is shown
// to the admin verbatim.
//
// Per-type workflows Task 9a: check_id/session_type_id/is_active are
// additive opt-ins for the per-type checks editor (Task 9b) -- absent,
// addCheck()'s Default call sites below stay byte-identical (stage_key +
// label only) to before these fields existed.
export interface SopCheckDefCreate {
  stage_key: string;
  label: string;
  check_id?: string;
  session_type_id?: string;
  is_active?: boolean;
}
// PATCH /v1/sop/defs/checks/{id} body. check_id/stage_key are immutable
// (rejected 400 IMMUTABLE_FIELD if present) and are deliberately absent
// from this type -- reword sends {label} only, reorder sends
// {sort_order} only, deactivate/reactivate send {is_active} only.
export interface SopCheckDefPatch {
  label?: string;
  sort_order?: number;
  is_active?: boolean;
}
export interface SopStageDefRow {
  stage_key: string;
  display_name: string;
  subtitle: string | null;
  sort_order: number;
  sla_default_hours: number;
  version: number;
  // Present on GET /v1/sop/defs and on the PATCH /stages/{key} result;
  // absent (never sent) only on the two check-only routes. updated_by is
  // null until a stage's first admin edit.
  updated_by: string | null;
  updated_at: string | null;
  // Absent on the PATCH /stages/{key} result (it returns the stage row
  // only, not its checks) — present on the GET /defs read.
  checks?: SopCheckDefRow[];
  // Per-type workflows Task 9a: present ONLY on GET /v1/sop/defs's typed
  // branch (?session_type_id=... given) — the robust source of truth for
  // Settings -> Workflow's per-field Inherited/Overridden tags. Absent on
  // the no-type response (byte-identical to before this existed) and on
  // every write-route response (PATCH/POST/restore never returns these).
  has_name_override?: boolean;
  has_subtitle_override?: boolean;
  has_sla_override?: boolean;
}
export interface SopDefsResponse {
  stages: SopStageDefRow[];
}
// PATCH body for /v1/sop/defs/stages/{key} (Workflow Admin Panel Task 5/7).
// Every field is optional — send only what changed. sla_default_hours is
// omitted entirely for the 'complete' stage (no SLA field for a terminal
// stage); the server validates 0..336 server-side regardless of the
// client-side pre-check in SectionWorkflow.vue.
// Per-type workflows Task 9a: session_type_id/revert_fields are additive
// opt-ins. ABSENT -> the Default write path, byte-identical to before
// these two fields existed. PRESENT (session_type_id) -> targets that
// type's override row instead (backend PATCH /defs/stages/{key}
// session_type_id branch — app/api/sop_defs.py::_update_stage_type_override).
// revert_fields only means anything alongside session_type_id: a subset of
// {"display_name","subtitle","sla_default_hours"} to clear back to
// inherit-the-Default (a field must never be both set and reverted in the
// same request — the server 400s CONFLICTING_FIELD).
export interface SopStageDefPatch {
  display_name?: string;
  subtitle?: string;
  sla_default_hours?: number;
  session_type_id?: string;
  revert_fields?: string[];
}
// One row of GET /v1/sop/defs/stages/{key}/versions (Workflow Admin Panel
// Task 9). This is the PRE-edit snapshot a PATCH /stages/{key} recorded
// right before that edit landed -- newest first. saved_by/saved_at name who
// made the edit that superseded this state and when.
export interface SopStageDefVersion {
  version: number;
  display_name: string;
  subtitle: string | null;
  sla_default_hours: number;
  saved_by: string;
  saved_at: string | null;
  // Per-type workflows Task 9a: present ONLY when stageVersions() was
  // called with opts.sessionTypeId (reading sop_stage_type_override_versions
  // instead of the Default history) -- absent on the Default history.
  has_name_override?: boolean;
  has_subtitle_override?: boolean;
  has_sla_override?: boolean;
}
// POST /v1/sop/defs/stages/{key}/restore response. `checks` is the stage's
// FULL current check list post-restore (SopCheckDefFull, not the GET-/defs
// subset) -- restore never deletes or creates check rows (documented roster
// limitation: a check added after the restored version is not removed), it
// only rolls back the label/sort_order/is_active of checks that exist right
// now. `restored_check_ids` names which of those checks actually changed.
//
// Per-type workflows Task 9a: when restoreStage() was called with
// opts.sessionTypeId, the response also carries a top-level
// session_type_id (additive -- not declared here since no current caller
// reads it; the Default shape above is unaffected).
export interface SopStageRestoreResponse {
  stage: SopStageDefRow;
  checks: SopCheckDefFull[];
  restored_check_ids: string[];
}
// DELETE /v1/sop/defs/checks/{id} response (per-type workflows Task 9a, for
// Task 9b's per-type checks editor) -- see app/api/sop_defs.py::delete_check_def.
export interface SopCheckDeleteResult {
  reverted: boolean;
  check_def_id: string;
  check_id: string;
  stage_key: string;
  session_type_id: string;
}

export const sop = {
  // Task 8 fix round 1: opts.includeInactive is an ADDITIVE opt-in.
  // Calling sop.defs() with no argument (every existing call site --
  // useSopDefs' singleton, SopView) is byte-identical to before this
  // option existed. Only Settings -> Workflow's checks editor passes
  // { includeInactive: true } so it can list a check deactivated in an
  // earlier browser session under "Show deactivated".
  //
  // Per-type workflows Task 4: opts.sessionTypeId is likewise ADDITIVE --
  // only useSopDefsForType's per-type reader (SopView) passes it, so the
  // backend merges Default + that type's overrides (empty override layer
  // today => byte-identical Default). Calling sop.defs() with no argument
  // at all remains query-string-free, exactly as before either option
  // existed.
  defs: (opts?: { includeInactive?: boolean; sessionTypeId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.includeInactive) params.set('include_inactive', '1');
    if (opts?.sessionTypeId) params.set('session_type_id', opts.sessionTypeId);
    const qs = params.toString();
    return http<SopDefsResponse>(`/v1/sop/defs${qs ? `?${qs}` : ''}`);
  },
  // Admin-only (Workflow Admin Panel Task 5 backend; Task 7 UI). Server
  // rejects stage_key/sort_order presence (400 IMMUTABLE_FIELD) and
  // out-of-range sla_default_hours (400 INVALID_SLA_HOURS, 0..336).
  updateStage: (stageKey: string, patch: SopStageDefPatch) =>
    http<SopStageDefRow>(
      `/v1/sop/defs/stages/${encodeURIComponent(stageKey)}`,
      { body: patch, method: 'PATCH' },
    ),
  // Admin-only (Workflow Admin Panel Task 5 backend; Task 8 UI). Label
  // only -- the server derives check_id via _check_slug and returns it on
  // the created row; the panel shows that server-minted id, never a
  // client-side guess.
  //
  // Per-type workflows Task 9a: `opts` is an ADDITIVE opt-in (all of Task
  // 9b's per-type check-write methods live here so 9b never has to touch
  // api.ts again). Calling addCheck(stageKey, label) with no third
  // argument sends exactly {stage_key, label} -- byte-identical to before
  // opts existed.
  addCheck: (
    stageKey: string,
    label: string,
    opts?: { sessionTypeId?: string; checkId?: string; isActive?: boolean },
  ) =>
    http<SopCheckDefFull>('/v1/sop/defs/checks', {
      body: {
        stage_key: stageKey,
        label,
        ...(opts?.checkId !== undefined ? { check_id: opts.checkId } : {}),
        ...(opts?.sessionTypeId !== undefined ? { session_type_id: opts.sessionTypeId } : {}),
        ...(opts?.isActive !== undefined ? { is_active: opts.isActive } : {}),
      } satisfies SopCheckDefCreate,
      method: 'POST',
    }),
  // Admin-only. PATCH by the row's UUID (SopCheckDefRow.id /
  // SopCheckDefFull.id) -- never by check_id, which the server rejects as
  // an immutable field if sent at all. One call per changed field group:
  // reword sends {label}, a reorder swap sends {sort_order} to each of the
  // two affected rows, deactivate/reactivate sends {is_active}.
  updateCheck: (checkDefId: string, patch: SopCheckDefPatch) =>
    http<SopCheckDefFull>(
      `/v1/sop/defs/checks/${encodeURIComponent(checkDefId)}`,
      { body: patch, method: 'PATCH' },
    ),
  // Admin-only (per-type workflows Task 9a, for Task 9b's per-type checks
  // editor). Hard-deletes a TYPE-scoped check row by its UUID -- reverts a
  // type's override of an inherited check, or removes a type-only check.
  // A Default row (session_type_id null) 400s CANNOT_DELETE_DEFAULT
  // server-side (Default checks are deactivated, never hard-deleted, so
  // attestations are preserved) -- see app/api/sop_defs.py::delete_check_def.
  deleteCheck: (checkDefId: string) =>
    http<SopCheckDeleteResult>(
      `/v1/sop/defs/checks/${encodeURIComponent(checkDefId)}`,
      { method: 'DELETE' },
    ),
  // Admin-only (Workflow Admin Panel Task 5 backend; Task 9 UI). Newest
  // first. An empty list is valid (a stage with no admin edits yet).
  //
  // Per-type workflows Task 9a: opts.sessionTypeId is an ADDITIVE opt-in --
  // absent, this is the Default history, byte-identical to before. Present,
  // it reads that TYPE's own override history instead (each entry also
  // carries has_name_override/has_subtitle_override/has_sla_override).
  stageVersions: (stageKey: string, opts?: { sessionTypeId?: string }) => {
    const params = new URLSearchParams();
    if (opts?.sessionTypeId) params.set('session_type_id', opts.sessionTypeId);
    const qs = params.toString();
    return http<SopStageDefVersion[]>(
      `/v1/sop/defs/stages/${encodeURIComponent(stageKey)}/versions${qs ? `?${qs}` : ''}`,
    );
  },
  // Admin-only. Restores stage fields (+ existing checks' field values,
  // per the documented roster limitation) to their state as of `version`.
  //
  // Per-type workflows Task 9a: opts.sessionTypeId is an ADDITIVE opt-in --
  // absent, this is the Default restore, byte-identical to before. Present,
  // it restores that TYPE's override row + the type's own checks instead
  // (the response then also carries a top-level session_type_id).
  restoreStage: (stageKey: string, version: number, opts?: { sessionTypeId?: string }) =>
    http<SopStageRestoreResponse>(
      `/v1/sop/defs/stages/${encodeURIComponent(stageKey)}/restore`,
      {
        body: opts?.sessionTypeId ? { version, session_type_id: opts.sessionTypeId } : { version },
        method: 'POST',
      },
    ),
  state: (sessionId: string) =>
    http<{ current_stage: string; is_blocked: boolean; blockers: unknown[]; assignees: unknown; sla_target_hours: unknown; entered_current_at: string | null; resolved_check_ids?: string[]; annotations?: SopAnnotation[] }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/sop`,
    ),
  // Per-session SLA override (admin). hours=null clears back to the default.
  updateSla: (sessionId: string, stage: string, hours: number | null) =>
    http<{ session_id: string; stage: string; hours: number | null; sla_target_hours: Record<string, number> }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/sop/sla`,
      { body: { stage, hours }, method: 'PATCH' },
    ),
  // Real stage-transition history (sop_transitions), oldest first.
  transitions: (sessionId: string) =>
    http<SopTransitionRow[]>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/sop/transitions`,
    ),
  // Non-session-scoped: per-stage counts + overdue counts for the dashboard.
  dashboardSummary: () =>
    http<Array<{ stage: string; count: number; overdue_count: number }>>(
      '/v1/sop/dashboard-summary',
    ),
  advance: (sessionId: string, toStage: string, note?: string) =>
    http(`/v1/sessions/${sessionId}/sop/advance`, { body: { to_stage: toStage, note }, method: 'POST' }),
  resolveCheck: (sessionId: string, checkId: string, label: string) =>
    http(`/v1/sessions/${sessionId}/sop/checks/resolve`, { body: { check_id: checkId, label }, method: 'POST' }),
  // Phase 6 — stage owner reassignment + annotations (note / override / blocker)
  assign: (sessionId: string, assignee: string, opts: { stage?: string; note?: string } = {}) =>
    http<{ session_id: string; stage: string; assignee: string; prev: unknown }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/sop/assign`,
      { body: { assignee, stage: opts.stage, note: opts.note }, method: 'POST' },
    ),
  annotate: (sessionId: string, body: string, opts: { stage?: string; kind?: 'note' | 'override' | 'blocker' } = {}) =>
    http<{
      session_id: string; stage: string; kind: string;
      annotation: { stage: string; kind: string; body: string; actor_email: string; inserted_at: string };
      total_count: number;
    }>(
      `/v1/sessions/${encodeURIComponent(sessionId)}/sop/annotations`,
      { body: { body, stage: opts.stage, kind: opts.kind || 'note' }, method: 'PATCH' },
    ),
};

// ─── Improvements ────────────────────────────────────────────────────────
export interface ImprovementSummary {
  id: string;
  title: string;
  status: string;
  risk: string;
  priority: string;
  submitted_at: string;
  submitted_by: string;
  is_security: boolean;
}

export const improvements = {
  list: (statusFilter?: string) =>
    http<ImprovementSummary[]>(`/v1/improvements${_q({ status_filter: statusFilter })}`),
  get: (id: string) =>
    http(`/v1/improvements/${encodeURIComponent(id)}`),
  suggest: (payload: { title: string; description?: string; type?: string; priority?: string; area?: string; is_security?: boolean }) =>
    http<ImprovementSummary>('/v1/improvements', { body: payload, method: 'POST' }),
  saveStep: (id: string, step: string, body_md: string) =>
    http(`/v1/improvements/${id}/wizard/${step}`, { body: { body_md }, method: 'PUT' }),
  admin: (id: string, patch: { status?: string; risk?: string; target_version?: string; admin_notes?: string }) =>
    http(`/v1/improvements/${id}`, { body: patch, method: 'PATCH' }),
  remove: (id: string) =>
    http(`/v1/improvements/${id}`, { method: 'DELETE' }),
};

// ─── Settings ────────────────────────────────────────────────────────────
export interface SettingsPerson {
  id: string; email: string; name: string;
  role?: string | null; avatar_color?: string | null; is_active?: boolean;
}
export interface SettingsGroup {
  id: string; name: string; description?: string | null;
}
export interface SettingsType {
  id: string; code: string; label: string;
  // Added by migration 038. The default Type can't be removed and seeds
  // new sessions' starting assignees once Unit 6 lands.
  is_default?: boolean;
}
export interface StageAssigneeRow {
  id?: string;
  stage: string;
  assignee_email: string;
  notify_email: boolean;
}

// Prompt template row from /v1/settings/templates (migration 047).
// kind='processing' templates encode STT preset configs in config.
// kind='ai_prompt' templates carry the Gemini system prompt in
// config.system_prompt.
// ai_mode strings a template can be marked default for. Mirrors the CHECK
// constraint in migration 049 and _DEFAULT_FOR_MODE_VALUES in settings.py.
export type AiModeDefault = 'transcript' | 'summary' | 'key-moments' | 'structured-notes';

export interface PromptTemplate {
  id:               string;
  kind:             'processing' | 'ai_prompt';
  name:             string;
  icon:             string;
  description:      string | null;
  category:         string;
  config:           Record<string, unknown>;
  is_system:        boolean;
  default_for_mode: AiModeDefault | null;
  version:          number;
  created_by:       string | null;
  created_at:       string | null;
  updated_at:       string | null;
}
export interface TemplateCreate {
  kind:              'processing' | 'ai_prompt';
  name:              string;
  icon?:             string;
  description?:      string;
  category?:         string;
  config?:           Record<string, unknown>;
  default_for_mode?: AiModeDefault | null;
}
export interface TemplatePatch {
  name?:             string;
  icon?:             string;
  description?:      string;
  category?:         string;
  config?:           Record<string, unknown>;
  // Explicit-null sets default_for_mode to NULL (unbinds). Field-absent
  // leaves it unchanged. Backend distinguishes via Pydantic model_fields_set.
  default_for_mode?: AiModeDefault | null;
}

export interface SettingsPersonPatch {
  email?:        string;
  name?:         string;
  role?:         string;
  avatar_color?: string;
  is_active?:    boolean;
}
export interface SettingsGroupPatch {
  name?:        string;
  description?: string;
}

// Auth user row from /v1/settings/auth-users. password_hash is NEVER
// returned by the API, so it's absent from this interface by design.
export interface AuthUser {
  id:                 string;
  email:              string;
  role:               'admin' | 'user';
  is_active:          boolean;
  last_login_at:      string | null;
  password_reset_at:  string | null;
  created_at:         string | null;
  updated_at:         string | null;
}
export interface AuthUserPatch {
  role?:      'admin' | 'user';
  is_active?: boolean;
}

// GET /v1/settings/pipeline-summary (app/api/settings.py::pipeline_summary).
// Read-only. Reports what sessions ARE on, measured from the database, plus
// what a NEW session starts on. There is no writable counterpart: nothing in
// the product sets `ai_pipeline` after the create-session call.
export interface PipelineCombination {
  transcript_provider: string;   // builtin | assemblyai (resolved, never empty)
  ai_pipeline:         string;   // direct | enhanced   (resolved, never empty)
  sessions:            number;
}
export interface PipelineSummary {
  // One row per combination that actually exists. A combination with no
  // sessions is ABSENT — the endpoint groups stored rows, it does not emit a
  // zero row for every possible pair.
  combinations:           PipelineCombination[];
  // What a new session starts on: the org setting when an admin chose one,
  // else the create-session schema default.
  default_provider:       string;
  default_pipeline:       string;
  // The combination ingest resolves a session with no stored record to. Read
  // from app/utils/pipeline_resolution.py, so it names the one `combinations`
  // row that holds every session counted below.
  fallback_provider:      string;
  fallback_pipeline:      string;
  // Sessions with no session_templates row. This counts stored rows, NOT
  // ingest runs: a session that has not reached ingest yet is counted too.
  missing_templates_rows: number;
}

export const settingsApi = {
  list: () => http<Record<string, unknown>>('/v1/settings'),
  pipelineSummary: () => http<PipelineSummary>('/v1/settings/pipeline-summary'),
  set: (key: string, value: unknown) =>
    http(`/v1/settings/${encodeURIComponent(key)}`, { body: { key, value }, method: 'PUT' }),
  people: () => http<SettingsPerson[]>('/v1/settings/people'),
  peopleAdd: (payload: { email: string; name: string; role?: string; avatar_color?: string }) =>
    http<SettingsPerson>('/v1/settings/people', { body: payload, method: 'POST' }),
  peopleUpdate: (id: string, patch: SettingsPersonPatch) =>
    http<SettingsPerson>(`/v1/settings/people/${encodeURIComponent(id)}`, { body: patch, method: 'PUT' }),
  peopleRemove: (id: string) =>
    http(`/v1/settings/people/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  groups: () => http<SettingsGroup[]>('/v1/settings/groups'),
  groupsAdd: (payload: { name: string; description?: string }) =>
    http<SettingsGroup>('/v1/settings/groups', { body: payload, method: 'POST' }),
  groupsUpdate: (id: string, patch: SettingsGroupPatch) =>
    http<SettingsGroup>(`/v1/settings/groups/${encodeURIComponent(id)}`, { body: patch, method: 'PUT' }),
  groupsRemove: (id: string) =>
    http(`/v1/settings/groups/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  groupMembers: (id: string) =>
    http<SettingsPerson[]>(`/v1/settings/groups/${encodeURIComponent(id)}/members`),
  groupMembersBulk: () =>
    http<Record<string, SettingsPerson[]>>('/v1/settings/groups-members'),
  // Phase 3 of the 2026-05-23 Settings BUILD plan. Streams the macro zip
  // bundle from /v1/settings/export/macro and triggers a browser download.
  // Uses fetch + blob (not window.location.href) so the JWT auth header is
  // present. Throws ApiError on 4xx/5xx so the caller can surface a clean
  // toast (e.g. 404 MACRO_NOT_FOUND when the bundle isn't deployed yet).
  downloadMacro: async (): Promise<void> => {
    const token = getToken();
    const resp = await fetch('/v1/settings/export/macro', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      const isJson = resp.headers.get('content-type')?.includes('application/json');
      const body = isJson ? await resp.json().catch(() => undefined) : await resp.text().catch(() => '');
      throw new ApiError(resp.status, body);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rounds-macros.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a tick to start the download before revoking the URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  groupMemberAdd: (groupId: string, personId: string) =>
    http<{ added: boolean }>(
      `/v1/settings/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(personId)}`,
      { method: 'POST' },
    ),
  groupMemberRemove: (groupId: string, personId: string) =>
    http(
      `/v1/settings/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(personId)}`,
      { method: 'DELETE' },
    ),
  types: () => http<SettingsType[]>('/v1/settings/types'),
  typesAdd: (payload: { code: string; label?: string }) =>
    http<SettingsType>('/v1/settings/types', { body: payload, method: 'POST' }),
  typesRemove: (id: string) =>
    http(`/v1/settings/types/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  typeAssignees: (id: string) =>
    http<StageAssigneeRow[]>(`/v1/settings/types/${encodeURIComponent(id)}/assignees`),
  setTypeAssignees: (id: string, rows: StageAssigneeRow[]) =>
    http<StageAssigneeRow[]>(
      `/v1/settings/types/${encodeURIComponent(id)}/assignees`,
      { body: { rows }, method: 'PUT' },
    ),
  // (The old settingsApi.emailTemplates() wrapper was removed in Phase 6 of
  // the 2026-05-23 Settings BUILD plan. The canonical endpoint moved to
  // /v1/email-templates - see emailTemplatesApi above.)

  // ─── Prompt templates (Settings → Prompt templates) ─────────────────
  // Phase 4 of the 2026-05-23 Settings BUILD plan. Backed by migration 047.
  // Two kinds share one table: 'processing' (STT preset configs) and
  // 'ai_prompt' (Gemini system prompts). kind-specific data lives in config.
  templatesList: (kind?: 'processing' | 'ai_prompt') =>
    http<PromptTemplate[]>(`/v1/settings/templates${kind ? `?kind=${kind}` : ''}`),
  templatesGet: (id: string) =>
    http<PromptTemplate>(`/v1/settings/templates/${encodeURIComponent(id)}`),
  templatesAdd: (payload: TemplateCreate) =>
    http<PromptTemplate>('/v1/settings/templates', { body: payload, method: 'POST' }),
  templatesUpdate: (id: string, patch: TemplatePatch) =>
    http<PromptTemplate>(`/v1/settings/templates/${encodeURIComponent(id)}`, { body: patch, method: 'PUT' }),
  templatesRemove: (id: string) =>
    http(`/v1/settings/templates/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // ─── Auth users (Settings → Auth & Logins) ──────────────────────────
  // Reset-only: passwords are never returned by any GET. The reset endpoint
  // accepts plaintext over TLS; the server bcrypt-hashes before storage.
  authUsersList: () =>
    http<AuthUser[]>('/v1/settings/auth-users'),
  authUsersAdd: (payload: { email: string; password: string; role?: 'admin' | 'user' }) =>
    http<AuthUser>('/v1/settings/auth-users', { body: payload, method: 'POST' }),
  authUsersUpdate: (id: string, patch: AuthUserPatch) =>
    http<AuthUser>(`/v1/settings/auth-users/${encodeURIComponent(id)}`, { body: patch, method: 'PUT' }),
  authUsersResetPassword: (id: string, password: string) =>
    http<{ email: string; password_reset_at: string }>(
      `/v1/settings/auth-users/${encodeURIComponent(id)}/reset-password`,
      { body: { password }, method: 'POST' },
    ),
  authUsersRemove: (id: string) =>
    http(`/v1/settings/auth-users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ─── Email templates (Settings → Email → Builder) ──────────────────────
// Phase 5 of the 2026-05-23 Settings BUILD plan. Backed by migration 048.
// Templates are scoped (session_type_id, stage_id, locale). NULL type_id
// means "default for all Types"; a per-Type row overrides the default for
// that specific Type.
export interface EmailTemplate {
  id:              string;
  session_type_id: string | null;   // null = default-for-all-types
  stage_id:        string;
  locale:          string;
  subject:         string;
  body:            string;
  created_by:      string | null;
  created_at:      string | null;
  updated_at:      string | null;
  // Only present on the resolve endpoint response.
  resolved_from?:  'per_type' | 'default';
}
export interface EmailTemplateCreate {
  session_type_id?: string | null;
  stage_id:         string;
  locale?:          string;
  subject:          string;
  body:             string;
}
export interface EmailTemplatePatch {
  subject?: string;
  body?:    string;
  locale?:  string;
}
export const emailTemplatesApi = {
  list: (params: { session_type_id?: string; stage_id?: string; include_defaults?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.session_type_id) q.set('session_type_id', params.session_type_id);
    if (params.stage_id)        q.set('stage_id', params.stage_id);
    if (params.include_defaults !== undefined) q.set('include_defaults', String(params.include_defaults));
    const qs = q.toString();
    return http<EmailTemplate[]>(`/v1/email-templates${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) =>
    http<EmailTemplate>(`/v1/email-templates/${encodeURIComponent(id)}`),
  add: (payload: EmailTemplateCreate) =>
    http<EmailTemplate>('/v1/email-templates', { body: payload, method: 'POST' }),
  update: (id: string, patch: EmailTemplatePatch) =>
    http<EmailTemplate>(`/v1/email-templates/${encodeURIComponent(id)}`, { body: patch, method: 'PUT' }),
  remove: (id: string) =>
    http(`/v1/email-templates/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  resolve: (payload: { session_type_id?: string | null; stage_id: string; locale?: string }) =>
    http<EmailTemplate>('/v1/email-templates/resolve', { body: payload, method: 'POST' }),
  // Phase 1 (2026-07-20) — the variable-contract registry drives the
  // builder chips; render + testSend go through the SERVER pipeline (the
  // same substitute + payload builder production uses), ending the
  // preview-vs-production drift class.
  variables: (kind?: string) =>
    http<TemplateVariable[]>(`/v1/email-templates/variables${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`),
  render: (payload: { stage_id: string; subject: string; body: string; session_id?: string | null }) =>
    http<TemplateRenderResult>('/v1/email-templates/render', { body: payload, method: 'POST' }),
  testSend: (payload: { stage_id: string; subject: string; body: string; to?: string; session_id?: string | null }) =>
    http<{ ok: boolean; to: string; payload_source: string; latency_ms: number | null; error: string | null }>(
      '/v1/email-templates/test-send', { body: payload, method: 'POST' }),
};

export interface TemplateVariable {
  name: string;
  category: string;
  producers: string[];
  required: boolean;
  sample: string;
  source: string;
  description: string;
}

export interface TemplateRenderResult {
  subject: string;
  html: string;
  payload_source: string;
  kind: string;
  gate: { unknown: string[]; empty_required: string[] };
  would_block: boolean;
}

// ─── Audit ───────────────────────────────────────────────────────────────
export const audit = {
  list: (params: { session_id?: string; actor?: string; kind?: string; limit?: number } = {}) =>
    http<unknown[]>(`/v1/audit${_q(params as Record<string, string | number | undefined>)}`),
  corrections: (sessionId: string) =>
    http<unknown[]>(`/v1/audit/sessions/${encodeURIComponent(sessionId)}/corrections`),
};

// ─── GCS upload ──────────────────────────────────────────────────────────
export const gcs = {
  signedUrl: (sessionId: string, filename: string, role?: string) =>
    http<{ signed_url: string; gcs_uri: string; blob_name: string }>(
      '/v1/gcs/upload-url',
      { body: { session_id: sessionId, filename, role }, method: 'POST' },
    ),
  uploadComplete: (sessionId: string, files: Array<{ gcs_uri: string; role?: string; filename?: string; content_type?: string; size_bytes?: number; duration_sec?: number }>) =>
    http<{ session_id: string; accepted: string[] }>(
      '/v1/gcs/upload-complete',
      { body: { session_id: sessionId, files }, method: 'POST' },
    ),
};

// ─── Email diagnostics (Phase 7 — admin-only SMTP probe + test send + log) ──
export interface SmtpConfigCheck {
  host:         { present: boolean; value: string | null };
  port:         { present: boolean; value: string | null };
  from_address: { present: boolean; value: string | null };
  username:     { present: boolean; value: null };
  password:     { present: boolean; value: null };
  // 2026-07-19 — real-send switches (api-service view; the worker's env is
  // authoritative for sends). Kept out of the SMTP row map above.
  flags?: {
    sop_deadline_email_enabled: boolean;
    sop_stage_email_enabled: boolean;
    note: string;
  };
}

export interface SmtpConnectivityStep {
  ok: boolean | null;
  latency_ms: number | null;
  error: string | null;
}

export interface SmtpConnectivityResult {
  connect: SmtpConnectivityStep;
  starttls: SmtpConnectivityStep;
  login: SmtpConnectivityStep;
  noop: SmtpConnectivityStep;
  quit: SmtpConnectivityStep;
}

export interface SmtpSendResult {
  sent: boolean;
  to: string;
  subject: string;
  latency_ms: number;
  error: string | null;
  smtp_log: string;
}

export interface EmailAttemptRow {
  id: string;
  attempted_at: string | null;
  from_address: string;
  to_address: string;
  subject: string | null;
  trigger: string;
  sop_session_id: string | null;
  stage: string | null;
  result: string;  // sent | failed | blocked | skipped | dead
  error_code: string | null;
  error_message: string | null;
  latency_ms: number | null;
  smtp_log: string | null;
  operator_email: string | null;
}

export interface NotificationRule {
  id?: string;
  session_type_id?: string | null;
  stage?: string | null;
  trigger?: string | null;
  enabled?: boolean | null;
  throttle_hours?: number | null;
  max_sends?: number | null;
  delay_minutes?: number | null;
  suppress_same_actor?: boolean | null;
  duplicate_window_hours?: number | null;
  cc: string[];
  bcc: string[];
  extra_recipients: string[];
  attachments: string[];
  capability_fallback?: boolean | null;
  retry_max?: number | null;
  retry_backoff_minutes?: number | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface CompletenessCell {
  stage: string; trigger: string; rule_enabled: boolean; template_exists: boolean;
  retry_max: number; suppress_same_actor: boolean; attachments: string[];
}

export const notificationsApi = {
  rules: () => http<{ rules: NotificationRule[]; defaults: Record<string, unknown> }>('/v1/notifications/rules'),
  putRule: (rule: NotificationRule) =>
    http<{ id: string; ok: boolean }>('/v1/notifications/rules', { body: rule, method: 'PUT' }),
  resend: (session_id: string, stage: string) =>
    http<{ enqueued: boolean }>('/v1/notifications/resend', { body: { session_id, stage }, method: 'POST' }),
  resendBulk: (body: { results?: string[]; since_hours?: number; dry_run?: boolean; limit?: number }) =>
    http<{ dry_run: boolean; count: number; targets: Array<{ session_id: string; stage: string }> }>(
      '/v1/notifications/resend-bulk', { body, method: 'POST' }),
  completeness: () => http<CompletenessCell[]>('/v1/notifications/completeness'),
};

export const emailDebug = {
  config: () => http<SmtpConfigCheck>('/v1/admin/email-debug/config'),
  connectivity: () => http<SmtpConnectivityResult>('/v1/admin/email-debug/connectivity', { method: 'POST' }),
  send: (body: { to: string; subject?: string; text_body?: string; html_body?: string }) =>
    http<SmtpSendResult>('/v1/admin/email-debug/send', { body, method: 'POST' }),
  attempts: (params: { limit?: number; to?: string; result?: string; since_hours?: number } = {}) =>
    http<EmailAttemptRow[]>(`/v1/admin/email-debug/attempts${_q(params as Record<string, string | number | undefined>)}`),
};


// ─── Diagnostics ─────────────────────────────────────────────────────────
export interface ClearSlotsResult {
  email: string;
  removed_count: number;
  removed_session_ids: string[];
  cap: number;
  remaining: number;
}

export interface GcsCheckRow {
  id:   string;          // "G1".."G14"
  name: string;
  ok:   boolean | null;  // null = deferred stub (not implemented yet)
  ms:   number;
  note: string | null;
}

// ── Queue / worker health (READ-ONLY broker observability) ──
export interface QueueTaskRow {
  task_id:    string;
  name:       string;
  session_id: string | null;
  runtime_s:  number | null;
  worker:     string | null;
  duplicate:  boolean;   // task_id appears >1× (acks_late redelivery signature)
  reserved:   boolean;   // prefetched-not-started vs actively running
}
export interface QueueWorkerRow {
  hostname:    string;
  concurrency: number | null;
  active:      number;
  reserved:    number;
  responded:   boolean;
}
export interface QueueHealth {
  ok:                   boolean;
  degraded:             boolean;   // probe blind — treat as UNKNOWN, never "healthy"
  workers:              QueueWorkerRow[];
  tasks:                QueueTaskRow[];
  total_concurrency:    number | null;
  active_total:         number;
  reserved_total:       number;
  free_slots:           number | null;
  queue_depth:          number | null;
  duplicates:           { task_id: string; name: string; count: number }[];
  saturated:            boolean;
  visibility_timeout_s: number;
  detail:               string | null;
}
export interface StuckSessionRow {
  session_id:        string;
  status:            string;
  minutes_in_status: number;
  last_audit_ts:     string | null;
  last_audit_actor:  string | null;
  starved:           boolean | null;   // true=starved, false=slow, null=probe blind
}
export interface RevokeTaskResult {
  task_id:   string;
  revoked:   boolean;
  terminate: boolean;
  detail:    string | null;
}
export interface FlushQueueResult {
  purged:     number;
  per_worker: Record<string, number> | null;
  detail:     string | null;
}
// SRT health (Settings → Diagnostics → SRT health). Read-only report over the
// srt_runs table + real AssemblyAI-vs-fallback coverage.
export interface SrtHealthRun {
  session_id:    string;
  code:          string;
  title:         string | null;
  status:        string;
  source:        string | null;
  segment_count: number | null;
  duration_ms:   number | null;
  span_ms:       number | null;
  error:         string | null;
  created_at:    string | null;
}
export interface SrtHealth {
  window_days: number;
  summary: {
    runs: number; ok: number; error: number; no_words: number; no_source: number;
    error_rate: number; avg_duration_ms: number; audio_hours: number;
  };
  coverage: {
    sessions_with_audio: number; sessions_with_aai: number;
    assemblyai_rate: number; fallback: number; pending: number;
  };
  recent: SrtHealthRun[];
}

export const diag = {
  gcs: () => http('/v1/diag/gcs'),
  classifyRoute: () => http('/v1/diag/classify-route'),
  health: () => http<{ status: string; version: string; env: string }>('/v1/health'),
  clearRateLimitSlots: () =>
    http<ClearSlotsResult>('/v1/diag/clear-rate-limit-slots', { method: 'POST' }),
  // Phase 2 of the 2026-05-23 Settings BUILD plan. Returns 14 rows: G1-G6 are
  // real probes, G7-G14 are deferred stubs (ok=null) so the UI is honest about
  // what's covered.
  gcsChecks: () => http<GcsCheckRow[]>('/v1/diag/gcs-checks'),
  // Admin-only escape hatch for the auth-users boot seed. Idempotent — if
  // the table already has rows the call returns {seeded:0}. Surfaced from
  // Settings → Auth & Logins empty-state recovery panel.
  reseedAuthUsers: () =>
    http<{ seeded: number; total: number; skipped_count: number }>(
      '/v1/diag/reseed-auth-users',
      { method: 'POST' },
    ),

  // ── Per-session operator rescue (Phase A5) ──
  // Routes documented in CLAUDE.md "Emergency operator commands". Each
  // is server-gated by CurrentUser; the editor's AdminTab also gates
  // the UI behind isAdmin as a UX guard.
  reingest:          (sessionId: string) => http<unknown>(`/v1/diag/reingest/${encodeURIComponent(sessionId)}`,            { method: 'POST' }),
  // realign is server-gated: /v1/diag/realign refuses without
  // ?confirm=<session_id>, because it re-times the transcript (it overwrites
  // segments.start_ms/end_ms on the default pipeline) and a curl bypasses
  // every browser confirm. The caller still shows its own dialog — the token
  // is the SERVER's gate, not a replacement for asking the operator.
  realign:           (sessionId: string) => http<unknown>(`/v1/diag/realign/${encodeURIComponent(sessionId)}?confirm=${encodeURIComponent(sessionId)}`, { method: 'POST' }),
  initSessionStages: (sessionId: string) => http<unknown>(`/v1/diag/init-session-stages/${encodeURIComponent(sessionId)}`, { method: 'POST' }),
  autoplacePolls:    (sessionId: string) => http<unknown>(`/v1/diag/autoplace-polls/${encodeURIComponent(sessionId)}`,     { method: 'POST' }),
  abortSession:      (sessionId: string) => http<unknown>(`/v1/diag/abort-session/${encodeURIComponent(sessionId)}`,       { method: 'POST' }),

  // ── Queue / worker health (READ-ONLY) + remediation wiring ──
  // queueHealth/stuckSessions are pure reads of the Celery control bus + a
  // staleness SQL. revokeTask/flushCeleryQueue wire the EXISTING server
  // routes (curl-only until now). revokeTask defaults terminate=false — that
  // discards an acks_late redelivery without SIGTERM-ing the original's
  // in-flight work; pass { terminate: true } only to force-kill (loses work).
  queueHealth:     () => http<QueueHealth>('/v1/diag/queue-health'),
  stuckSessions:   (minutes = 10) => http<StuckSessionRow[]>(`/v1/diag/stuck-sessions?minutes=${encodeURIComponent(minutes)}`),
  // SRT-pass health (READ-ONLY): run totals, AssemblyAI-vs-fallback coverage,
  // recent runs. Powers Settings → Diagnostics → SRT health.
  srtHealth:       (days = 30) => http<SrtHealth>(`/v1/diag/srt-health?days=${encodeURIComponent(days)}`),
  // Operator: enqueue the AssemblyAI SRT pass for up to `limit` existing ready
  // sessions with no AssemblyAI SRT yet. Bounded + idempotent — call per batch.
  srtBackfill:     (limit = 25) => http<{ status: string; limit: number }>(`/v1/diag/srt-backfill?limit=${encodeURIComponent(limit)}`, { method: 'POST' }),
  revokeTask:      (taskId: string, opts: { terminate?: boolean } = {}) =>
    http<RevokeTaskResult>(`/v1/diag/revoke-task/${encodeURIComponent(taskId)}?terminate=${opts.terminate ? 'true' : 'false'}`, { method: 'POST' }),
  flushCeleryQueue: () => http<FlushQueueResult>('/v1/diag/flush-celery-queue', { method: 'POST' }),
};

export type Api = typeof auth & typeof sessions; // ergonomic export
